<template>
  <main class="login-page">
    <section class="login-intro">
      <div class="login-logo" aria-label="Cotisation Section Lyon">
        <div class="login-monogram" aria-hidden="true">
          <span>C</span><span>S</span><span>L</span>
        </div>
        <strong>Section Lyon</strong>
      </div>
      <div>
        <span class="eyebrow">Espace trésorerie</span>
        <h1>Une gestion financière claire et sécurisée.</h1>
        <p>
          Retrouvez les cotisations, les bilans et les rappels dans un espace
          réservé aux utilisateurs autorisés.
        </p>
      </div>
      <small
        ><i class="bi bi-shield-check"></i> Session privée · Données
        locales</small
      >
    </section>

    <section class="login-panel">
      <form class="login-card" @submit.prevent="submit">
        <div>
          <span class="eyebrow">Authentification</span>
          <h2>Bienvenue</h2>
          <p>Connectez-vous avec votre identifiant ou votre adresse mail.</p>
        </div>
        <div class="field">
          <label for="login">Identifiant ou adresse mail</label>
          <input
            id="login"
            v-model.trim="credentials.login"
            class="input"
            autocomplete="username"
            autofocus
            required
          />
        </div>
        <div class="field">
          <label for="password">Mot de passe</label>
          <div class="password-field">
            <input
              id="password"
              v-model="credentials.password"
              class="input"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="current-password"
              required
            />
            <button
              type="button"
              class="password-toggle"
              :aria-label="
                showPassword
                  ? 'Masquer le mot de passe'
                  : 'Afficher le mot de passe'
              "
              @click="showPassword = !showPassword"
            >
              <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
            </button>
          </div>
        </div>
        <button class="button login-button" :disabled="loading">
          <span>{{ loading ? "Connexion…" : "Se connecter" }}</span>
          <i class="bi bi-arrow-right"></i>
        </button>
      </form>
    </section>
  </main>
</template>

<script setup lang="ts">
import { reactive, ref } from "vue";
import { login } from "@/Shared/services/authService";
import { toast } from "@/Shared/services/toastService";

const credentials = reactive({ login: "", password: "" });
const loading = ref(false);
const showPassword = ref(false);

async function submit() {
  loading.value = true;
  try {
    await login(credentials.login, credentials.password);
  } catch (exception) {
    toast.danger((exception as Error).message);
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: grid;
  grid-template-columns: minmax(360px, 0.9fr) minmax(480px, 1.1fr);
  background: var(--canvas);
}
.login-intro {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 52px 58px;
  color: #f7f4e9;
  background: var(--green);
}
.login-logo {
  width: max-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #f7f1e3;
}
.login-monogram {
  position: relative;
  width: 80px;
  height: 48px;
  color: #ddc692;
  font-family: Georgia, "Times New Roman", serif;
  line-height: 1;
}
.login-monogram span {
  position: absolute;
  font-size: 42px;
  font-weight: 400;
}
.login-monogram span:first-child {
  left: 7px;
  top: 4px;
}
.login-monogram span:nth-child(2) {
  left: 29px;
  top: 0;
  z-index: 2;
  color: #f7f1e3;
  font-size: 39px;
  font-style: italic;
}
.login-monogram span:last-child {
  left: 49px;
  top: 5px;
  font-size: 41px;
}
.login-logo strong {
  margin-top: 5px;
  font: 400 18px Georgia, "Times New Roman", serif;
}
.login-intro > div:nth-child(2) {
  max-width: 520px;
}
.login-intro .eyebrow {
  color: #ddc692;
}
.login-intro h1 {
  margin: 13px 0 16px;
  font: 600 clamp(30px, 4vw, 48px) Manrope;
  line-height: 1.12;
  letter-spacing: -1.8px;
}
.login-intro p {
  max-width: 470px;
  margin: 0;
  color: #b9c9c2;
  font-size: 16px;
  line-height: 1.65;
}
.login-intro > small {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #aebeb7;
  font-size: 12px;
}
.login-panel {
  display: grid;
  place-items: center;
  padding: 40px;
}
.login-card {
  width: min(440px, 100%);
  display: grid;
  gap: 20px;
  padding: 36px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: var(--surface);
  box-shadow: var(--shadow);
}
.login-card h2 {
  margin: 6px 0 5px;
  font: 600 27px Manrope;
}
.login-card > div:first-child p {
  margin: 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.5;
}
.password-field {
  position: relative;
}
.password-field .input {
  padding-right: 43px;
}
.password-toggle {
  position: absolute;
  top: 50%;
  right: 6px;
  width: 33px;
  height: 33px;
  display: grid;
  place-items: center;
  border: 0;
  color: var(--muted);
  background: transparent;
  transform: translateY(-50%);
}
.login-button {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 3px;
  padding: 12px 15px;
}
.login-button i {
  margin: 0;
}
@media (max-width: 800px) {
  .login-page {
    display: block;
  }
  .login-intro {
    min-height: auto;
    padding: 30px 24px;
  }
  .login-intro > div:nth-child(2),
  .login-intro > small {
    display: none;
  }
  .login-panel {
    min-height: calc(100vh - 130px);
    padding: 24px 16px;
  }
  .login-card {
    padding: 27px 22px;
  }
}
</style>
